import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../../../redux/store";
import Header from "../../../../components/Header/Header";
import SearchBar from "../../../../components/SearchBar/SearchBar";
import Input from "../../../../components/Input/Input";
import SearchIcon from "../../../../assets/svg/search-icon";
import { ViewToggle } from "../KanbanGrid/components/ViewToggle";
import { visibleScrollbar } from "../../../../utils/functions/get-visible-scrollbar";
import PlayIcon from "../../../../assets/svg/play-icon";
import InfoBtnIcon from "../../../../assets/svg/info-btn-icon";
import GridIcon from "../../../../assets/svg/grid-icon";
import ListViewIcon from "../../../../assets/svg/list-view-icon";
import DealKanbanCard from "./components/DealKanbanCard";
import DealTableListing from "./components/DealTableListing";
import { DealsActionBar } from "./components/DealsActionBar";
import { getCurrencySymbol } from "../../../../utils/functions/get-currency-symbols";
import type { DealStage, Deal, DealsBoardParams } from "./utils/types";
import {
  setDealStages,
  setDealsInitialLoad,
  setDealsActiveView,
  setDealsViews,
  setDealsSelectedViewId,
  setNewlyCreatedDealsViewId,
  removeDealsView,
  appendStageDealsList,
  setCollapsedStageKeys,
  toggleCollapsedStageKey,
  clearCollapsedStageKeys,
  setDealsAutoCollapseSignature,
  setDealsStagesLoadedSignature,
} from "../../../../redux/slices/dealsSlice";
import { normalizeDealsFilterGroups } from "./utils/dealsViewConstants";
import { setDrawer as setCrmDrawer } from "../../../../redux/slices/drawerSlice";
import { DealsCreateEditView } from "./components/DealsCreateEditView";
import CrmDeleteViewModal from "../KanbanGrid/components/CrmDeleteViewModal";
import ExportContactsModal from "../KanbanGrid/components/ExportContactsModal";
import ExportQueuedModal from "../KanbanGrid/components/ExportQueuedModal";
import { useCreateDataExportMutation } from "../../../../redux/services/settingsApi";
import LeadStagesShimmer from "../KanbanGrid/components/LeadListShimmer/LeadStagesShimmer";
import PulseLoader from "../../../../components/Loader/PulseLoader";
import {
  useLazyGetDealsBoardQuery,
  useLazyGetDealsListQuery,
  useGetDealsViewsQuery,
  useDeleteDealsViewMutation,
  useUpdateDealMutation,
} from "../../../../redux/services/dealsApi";
import { customToast } from "../../../../utils/custom-toast/custom-toast";
import { Tooltip } from "../../../../components/Tooltip/Tooltip";
import { useDebounce } from "../../../../hooks/useDebounce";
import { DEAL_LABELS } from "./utils/constants";

const DealsLayout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const stages = useSelector((state: RootState) => state.deals.stages);
  const isInitialLoad = useSelector(
    (state: RootState) => state.deals.isInitialLoad
  );
  const activeView = useSelector((state: RootState) => state.deals.activeView);
  const dealsViews = useSelector((state: RootState) => state.deals.dealsViews);
  const selectedViewId = useSelector(
    (state: RootState) => state.deals.selectedViewId
  );
  const newlyCreatedDealsViewId = useSelector(
    (state: RootState) => state.deals.newlyCreatedDealsViewId
  );
  const viewRefetchTrigger = useSelector(
    (state: RootState) => state.deals.viewRefetchTrigger
  );
  const collapsedStageKeys = useSelector(
    (state: RootState) => state.deals.collapsedStageKeys
  );
  const autoCollapseSignature = useSelector(
    (state: RootState) => state.deals.autoCollapseSignature
  );
  const stagesLoadedSignature = useSelector(
    (state: RootState) => state.deals.stagesLoadedSignature
  );

  const [dealSearch, setDealSearch] = useState("");
  const debouncedSearch = useDebounce(dealSearch.trim(), 400);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportQueuedOpen, setIsExportQueuedOpen] = useState(false);
  const [exportedFile, setExportedFile] = useState<{
    name: string;
    format: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
    emoji_icon?: string;
  } | null>(null);
  const [isDeletingView, setIsDeletingView] = useState(false);
  const [sharedOwnerIds, setSharedOwnerIds] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | undefined>(
    undefined
  );
  const [isBoardLoading, setIsBoardLoading] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [loadingStages, setLoadingStages] = useState<Record<string, boolean>>(
    {}
  );
  const fetchingStagesRef = useRef<Record<string, boolean>>({});
  const stagePageRef = useRef<Record<string, number>>({});

  const boardViewId =
    selectedViewId !== "all_deals" ? selectedViewId : undefined;

  const [fetchDealsBoard] = useLazyGetDealsBoardQuery();
  const [fetchDealsList] = useLazyGetDealsListQuery();
  const { data: dealsViewsData } = useGetDealsViewsQuery(
    {
      page: 1,
      limit: 100,
    },
    { refetchOnMountOrArgChange: true }
  );
  const [deleteDealsViewApi] = useDeleteDealsViewMutation();
  const [updateDeal] = useUpdateDealMutation();
  const [createDataExport, { isLoading: isExporting }] =
    useCreateDataExportMutation();

  const buildBoardParams = useCallback((): DealsBoardParams => {
    const hasUnassigned = sharedOwnerIds.includes("unassigned");
    const agentIds = sharedOwnerIds.filter((id) => id !== "unassigned");
    const params: DealsBoardParams = {};
    if (boardViewId) params.view_id = boardViewId;
    if (agentIds.length > 0) params.assignee = agentIds;
    if (hasUnassigned) params.is_unassigned = true;
    if (sortKey) params.sort_key = sortKey;
    if (sortOrder) params.sort_order = sortOrder;
    if (debouncedSearch) params.search = debouncedSearch;
    params.limit_per_stage = 10;
    return params;
  }, [boardViewId, sharedOwnerIds, sortKey, sortOrder, debouncedSearch]);

  const handleExport = useCallback(
    async ({
      exportName,
      fileFormat,
    }: {
      exportName: string;
      fileFormat: string;
    }) => {
      const entity_filter: Record<string, unknown> = {};
      if (boardViewId) {
        entity_filter.view_id = boardViewId;
      }
      const hasUnassigned = sharedOwnerIds.includes("unassigned");
      const agentIds = sharedOwnerIds.filter((id) => id !== "unassigned");
      if (agentIds.length > 0) entity_filter.assignee = agentIds;
      if (hasUnassigned) entity_filter.unassigned = true;
      if (dealSearch.trim()) entity_filter.search = dealSearch.trim();
      try {
        await createDataExport({
          entity_type: "deal",
          file_format: fileFormat,
          file_name_hint: exportName,
          entity_filter,
        }).unwrap();
        setIsExportModalOpen(false);
        setExportedFile({
          name: `${exportName}.${fileFormat}`,
          format: fileFormat,
        });
        setIsExportQueuedOpen(true);
      } catch {
        customToast("Failed to start export. Please try again.", "error");
      }
    },
    [boardViewId, sharedOwnerIds, dealSearch, createDataExport]
  );

  const fetchDealsData = useCallback(async () => {
    setIsBoardLoading(true);
    try {
      const result = await fetchDealsBoard(buildBoardParams()).unwrap();
      dispatch(setDealStages(result?.stages ?? []));
      setLastSyncedAt(result?.last_synced_at ?? null);
      dispatch(
        setDealsStagesLoadedSignature(
          `${boardViewId ?? "all_deals"}|${viewRefetchTrigger}`
        )
      );
      stagePageRef.current = {};
    } catch {
      // API failed — leave stages empty
    } finally {
      setIsBoardLoading(false);
      dispatch(setDealsInitialLoad(false));
    }
  }, [
    fetchDealsBoard,
    buildBoardParams,
    dispatch,
    boardViewId,
    viewRefetchTrigger,
  ]);

  useEffect(() => {
    fetchDealsData();
  }, [fetchDealsData, viewRefetchTrigger]);

  useEffect(() => {
    if (dealsViewsData?.views) {
      dispatch(setDealsViews(dealsViewsData.views));
    }
  }, [dealsViewsData, dispatch]);

  useEffect(() => {
    if (newlyCreatedDealsViewId) {
      dispatch(setDealsSelectedViewId(newlyCreatedDealsViewId));
      dispatch(setNewlyCreatedDealsViewId(null));
    }
  }, [newlyCreatedDealsViewId, dispatch]);

  const handleConfirmDeleteView = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeletingView(true);
    try {
      await deleteDealsViewApi(deleteTarget.id).unwrap();
      dispatch(removeDealsView(deleteTarget.id));
      if (selectedViewId === deleteTarget.id) {
        dispatch(setDealsSelectedViewId("all_deals"));
      }
      customToast("View deleted successfully", "success");
      setDeleteTarget(null);
    } catch {
      customToast("Failed to delete view", "error");
    } finally {
      setIsDeletingView(false);
    }
  }, [deleteTarget, deleteDealsViewApi, dispatch, selectedViewId]);

  const handleKanbanLoadMore = useCallback(
    async (stageKey: string) => {
      if (fetchingStagesRef.current[stageKey]) return;

      const stage = stages.find((s) => s.stage_key === stageKey);
      if (!stage || !stage.has_more) return;

      fetchingStagesRef.current[stageKey] = true;
      setLoadingStages((prev) => ({ ...prev, [stageKey]: true }));

      const currentPage = stagePageRef.current[stageKey] ?? 1;
      const nextPage = currentPage + 1;

      const hasUnassigned = sharedOwnerIds.includes("unassigned");
      const agentIds = sharedOwnerIds.filter((id) => id !== "unassigned");

      try {
        const result = await fetchDealsList({
          stage: stageKey,
          page: nextPage,
          limit: 10,
          view_id: boardViewId,
          assignee: agentIds.length > 0 ? agentIds : undefined,
          is_unassigned: hasUnassigned || undefined,
          sort_key: sortKey,
          sort_order: sortOrder,
        }).unwrap();

        dispatch(
          appendStageDealsList({
            stageKey,
            deals: result?.deals ?? [],
            total: result?.total ?? 0,
            hasMore: result?.has_next ?? false,
          })
        );
        stagePageRef.current[stageKey] = nextPage;
      } catch {
        // user can retry by scrolling again
      } finally {
        fetchingStagesRef.current[stageKey] = false;
        setLoadingStages((prev) => ({ ...prev, [stageKey]: false }));
      }
    },
    [
      stages,
      fetchDealsList,
      boardViewId,
      sharedOwnerIds,
      sortKey,
      sortOrder,
      dispatch,
    ]
  );

  const handleKanbanScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>, stageKey: string) => {
      if (fetchingStagesRef.current[stageKey]) return;
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 50) {
        handleKanbanLoadMore(stageKey);
      }
    },
    [handleKanbanLoadMore]
  );

  const toggleStageCollapse = useCallback(
    (stageKey: string) => {
      dispatch(toggleCollapsedStageKey(stageKey));
    },
    [dispatch]
  );

  const handleDealSelect = useCallback(
    (dealId: string) => {
      console.log("dealId", dealId);
      navigate(`/dashboard/crm/bookings/${dealId}`);
    },
    [navigate]
  );

  const handleAssignAgent = useCallback(
    async (contractId: string, ids: string[]) => {
      const assignee = ids?.[0] ?? null;
      await updateDeal({ contractId, body: { assignee } });
      fetchDealsData();
    },
    [updateDeal, fetchDealsData]
  );

  const handleSearch = useCallback(
    async (query: string) => {
      if (!query.trim()) return [];

      try {
        const result = await fetchDealsBoard({
          search: query,
        })?.unwrap();
        const allDeals = (result?.stages ?? []).flatMap((s) => s?.deals ?? []);
        return allDeals.map((deal: Deal) => ({
          id: deal?.contract_id,
          value: deal?.deal_title,
          content: (
            <div className="p-1 flex items-center justify-between">
              <p className="text-data dark:text-data-dk font-medium text-md truncate">
                {deal.deal_title}
              </p>
              <p className="text-md text-dark-gray dark:text-dark-gray-dk font-normal shrink-0">
                {deal.property_name}
              </p>
            </div>
          ),
        }));
      } catch {
        return [];
      }
    },
    [fetchDealsBoard, boardViewId]
  );

  const handleOwnerApply = useCallback((ids: string[]) => {
    setSharedOwnerIds(ids);
  }, []);

  const handleOwnerReset = useCallback(() => {
    setSharedOwnerIds([]);
  }, []);

  const handleSort = useCallback(
    (key: string, order: "asc" | "desc" | undefined) => {
      if (!order) {
        setSortKey(undefined);
        setSortOrder(undefined);
      } else {
        setSortKey(key);
        setSortOrder(order);
      }
    },
    []
  );

  const filteredStages: DealStage[] = useMemo(() => stages, [stages]);

  const selectedView = useMemo(
    () => dealsViews?.find((v) => v?.id === selectedViewId),
    [dealsViews, selectedViewId]
  );

  useEffect(() => {
    const stagesReady = (stages?.length ?? 0) > 0;
    if (!stagesReady) return;

    const targetSignature = `${selectedViewId}|${viewRefetchTrigger}`;
    // Gate on the freshness marker — only auto-collapse against stage data
    // that was actually loaded for the current (view, refresh) tuple.
    if (stagesLoadedSignature !== targetSignature) return;
    if (autoCollapseSignature === targetSignature) return;

    const filterGroups = selectedView?.filter_groups
      ? normalizeDealsFilterGroups(
          selectedView.filter_groups as Parameters<
            typeof normalizeDealsFilterGroups
          >[0]
        )
      : [];
    const hasStageFilter = filterGroups?.some((g) =>
      g?.conditions?.some(
        (c) => c?.key === "stage" && (c?.value?.length ?? 0) > 0
      )
    );

    if (hasStageFilter) {
      const zeroCountKeys = (stages ?? [])
        .filter((s) => (s?.total ?? 0) === 0)
        .map((s) => s?.stage_key)
        .filter((key): key is string => Boolean(key));
      dispatch(setCollapsedStageKeys(zeroCountKeys));
    } else {
      dispatch(clearCollapsedStageKeys());
    }
    dispatch(setDealsAutoCollapseSignature(targetSignature));
  }, [
    selectedViewId,
    viewRefetchTrigger,
    selectedView,
    stages,
    autoCollapseSignature,
    stagesLoadedSignature,
    dispatch,
  ]);

  const totalDealCount = useMemo(
    () => stages?.reduce((sum, s) => sum + (s?.total ?? 0), 0) ?? 0,
    [stages]
  );

  const handleViewChange = useCallback(
    (viewId: string) => {
      dispatch(setDealsSelectedViewId(viewId));
    },
    [dispatch]
  );

  const showShimmer = isInitialLoad || isBoardLoading;

  const renderKanbanView = () => (
    <div
      className={`flex flex-1 min-h-0 rounded-[6px] overflow-x-auto overflow-y-hidden ${visibleScrollbar(
        "[&::-webkit-scrollbar]:h-[8px] [&::-webkit-scrollbar]:w-[8px]"
      )}`}
    >
      {showShimmer ? (
        <div className="p-3 flex gap-[6px]">
          {Array.from({ length: 6 }).map((_, i) => (
            <LeadStagesShimmer key={i} />
          ))}
        </div>
      ) : (
        <div className="flex gap-[6px]">
          {filteredStages?.map((stage) => {
            const isCollapsed = collapsedStageKeys?.includes(stage?.stage_key);
            return (
              <div
                key={stage?.stage_key}
                className={`shrink-0 flex flex-col gap-[6px] ${
                  isCollapsed ? "h-full" : ""
                }`}
              >
                {!isCollapsed && (
                  <div
                    className="w-[300px] h-[26px] rounded-[8px] px-[12px] flex justify-between items-center"
                    style={{ backgroundColor: stage?.secondary_hex_code }}
                  >
                    <div className="flex items-center gap-[8px]">
                      <p className="uppercase text-md font-bold text-fixed-white dark:text-fixed-white-dk leading-normal">
                        {stage?.stage_title}
                      </p>
                      <div className="bg-black/15 min-w-[18px] px-[6px] rounded-[24px] flex items-center">
                        <p className="text-sm font-semibold text-fixed-white dark:text-fixed-white-dk text-center flex-1 leading-normal">
                          {stage?.total?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleStageCollapse(stage?.stage_key)}
                    >
                      <PlayIcon className="w-[16px] h-[16px] text-fixed-white dark:text-fixed-white-dk rotate-180" />
                    </button>
                  </div>
                )}

                {isCollapsed ? (
                  <>
                    <button
                      onClick={() => toggleStageCollapse(stage?.stage_key)}
                      className="flex items-center px-[8px] py-[12px] rounded-[8px] cursor-pointer max-w-[26px] w-[26px]"
                      style={{ backgroundColor: stage?.secondary_hex_code }}
                    >
                      <div className="flex flex-col gap-[6px] items-center justify-center w-full">
                        <PlayIcon className="w-[16px] h-[16px] text-fixed-white dark:text-fixed-white-dk shrink-0" />
                        <div
                          className="flex items-center justify-center"
                          style={{ writingMode: "vertical-rl" }}
                        >
                          <div
                            className="flex items-center gap-[8px]"
                            style={{ transform: "rotate(180deg)" }}
                          >
                            <span className="uppercase text-xs font-bold text-fixed-white dark:text-fixed-white-dk whitespace-nowrap">
                              {stage?.stage_title}
                            </span>
                            <div className="bg-black/15 px-[6px] rounded-[24px]">
                              <span className="text-sm font-semibold text-fixed-white dark:text-fixed-white-dk leading-normal">
                                {stage?.total?.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                    <div className="flex-1" />
                  </>
                ) : (
                  <>
                    <div
                      className="flex flex-col gap-[8px] overflow-y-auto overflow-x-hidden no-scrollbar flex-1 min-h-0"
                      data-crm-stage-scroll-container
                      onScroll={(e) => handleKanbanScroll(e, stage?.stage_key)}
                    >
                      {stage?.deals?.map((deal) => (
                        <DealKanbanCard
                          key={deal?.contract_id}
                          deal={deal}
                          onSelect={handleDealSelect}
                          onAssignAgent={handleAssignAgent}
                        />
                      ))}
                      {loadingStages[stage?.stage_key] && (
                        <div className="flex items-center justify-center pt-2">
                          <PulseLoader size={6} />
                        </div>
                      )}
                    </div>
                    {stage?.total_amount && (
                      <div className="w-[300px] shrink-0 rounded-[8px] bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk px-[12px] py-[8px] flex flex-col gap-[2px]">
                        <div className="flex items-center gap-[4px]">
                          <span className="text-sm font-bold text-verba-indigo dark:text-verba-indigo-dk">
                            {stage.total_amount}
                          </span>
                          <span className="text-sm font-normal text-heading-text dark:text-heading-text-dk">
                            | Total amount
                          </span>
                        </div>
                        <div className="flex items-center gap-[4px]">
                          <span className="text-sm font-bold text-verba-indigo dark:text-verba-indigo-dk">
                            {stage.weighted_amount}
                          </span>
                          {stage?.weighted_amount_percentage && (
                            <span className="text-sm font-normal text-analytics-gray dark:text-analytics-gray-dk">
                              ({stage.weighted_amount_percentage})
                            </span>
                          )}
                          <span className="text-sm font-normal text-heading-text dark:text-heading-text-dk">
                            | Weighted amount
                          </span>
                          <Tooltip
                            content={
                              <div className="flex flex-col gap-[10px] min-w-[160px]">
                                <p className="text-sm font-semibold uppercase leading-[18px]">
                                  {stage.stage_title}
                                </p>
                                <p className="text-xs font-medium leading-[1.5]">
                                  Company currency
                                </p>
                                <div className="flex flex-col gap-[4px]">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-normal leading-[1.5]">
                                      Total
                                    </span>
                                    <span className="text-xs font-semibold leading-[1.5]">
                                      {stage.total_amount}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-normal leading-[1.5]">
                                      Weighted
                                      {stage?.weighted_amount_percentage
                                        ? ` (${stage.weighted_amount_percentage})`
                                        : ""}
                                    </span>
                                    <span className="text-xs font-semibold leading-[1.5]">
                                      {stage.weighted_amount}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            }
                            side="top"
                            align="center"
                            contentClassName="text-xs !px-[14px] !py-[14px]"
                          >
                            <div>
                              <InfoBtnIcon className="w-[18px] h-[18px] text-analytics-gray dark:text-analytics-gray-dk shrink-0" />
                            </div>
                          </Tooltip>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="flex flex-col w-full h-full bg-pure-white dark:bg-pure-white-dk">
        <Header>
          <div className="flex items-center">
            <div className="flex items-center gap-[8px]">
              <h1 className="text-xl font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                CRM
              </h1>
              <hr className="border border-border-gray dark:border-border-gray-dk h-[16px] w-1px" />
              <p className="text-md font-normal text-heading-text dark:text-heading-text-dk">
                {DEAL_LABELS.plural}
              </p>
            </div>

            <div className="w-[280px] mx-auto">
              <SearchBar
                id="crm-deal-search"
                placeholder={DEAL_LABELS.searchPlaceholder}
                noSuggestionsText={DEAL_LABELS.noSearchResults}
                enableApiSearch={true}
                onApiSearch={handleSearch}
                onSelect={(_suggestion, item) =>
                  handleDealSelect(String(item?.id ?? ""))
                }
                apiSearchDebounceMs={500}
                minCharsToSearch={1}
                suggestionsClassName="max-h-[300px]"
                containerClassName="w-[280px]"
                className="pl-[8px] pr-[8px] pt-[7px] pb-[7px] hover:border-b-2 hover:border-b-verba-blue-dark-mode dark:hover:border-b-verba-blue hover:rounded-[6px]"
              />
            </div>
          </div>
        </Header>

        <div className="flex flex-col gap-[14.5px] px-4 pt-4 w-full h-full overflow-hidden border-l border-t rounded-tl-[6px] border-border-gray dark:border-border-gray-dk">
          <DealsActionBar
            activeView={activeView}
            onViewChange={(v) => dispatch(setDealsActiveView(v))}
            onAddViewClick={() =>
              dispatch(setCrmDrawer({ open: true, type: "deals_view" }))
            }
            dealsViews={dealsViews}
            selectedViewId={selectedViewId}
            onSelectView={handleViewChange}
            onDeleteView={setDeleteTarget}
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                {selectedViewId === "all_deals"
                  ? DEAL_LABELS.allItems
                  : dealsViews?.find((v) => v.id === selectedViewId)?.name}
              </h2>
              <span className="text-xxs font-medium text-heading-text dark:text-heading-text-dk border border-border-gray dark:border-border-gray-dk rounded-sm px-[6px] py-px">
                {totalDealCount.toLocaleString()}
              </span>

              {activeView !== "list" && (
                <>
                  <div className="w-px h-[16px] bg-border-gray dark:bg-border-gray-dk shrink-0" />
                  <Input
                    id="deals-inline-search"
                    placeholder={DEAL_LABELS.inlineSearchPlaceholder}
                    value={dealSearch}
                    onChange={(e) => setDealSearch(e.target.value)}
                    leftIcon={SearchIcon}
                    iconClassName="w-[14px] h-[14px] text-gray-placeholder dark:text-gray-placeholder-dk"
                    secondaryContainerClassName="h-[26px] px-[8px] border border-border-gray dark:border-border-gray-dk rounded-[6px]"
                    containerClassName="w-[150px]"
                    inputClassName="text-sm"
                  />
                </>
              )}

              <div className="w-px h-[16px] bg-border-gray dark:bg-border-gray-dk shrink-0" />
              <div className="flex items-center gap-[6px]">
                <InfoBtnIcon className="w-[14px] h-[14px] shrink-0 text-analytics-gray dark:text-analytics-gray-dk" />
                <span className="text-sm font-normal text-analytics-gray dark:text-analytics-gray-dk whitespace-nowrap">
                  Last synced
                </span>
                <span className="text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
                  {lastSyncedAt
                    ? new Date(lastSyncedAt).toLocaleString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : "-"}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsExportModalOpen(true)}
                className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk border border-border-gray dark:border-border-gray-dk rounded-md px-3 py-1 hover:bg-hover-effect dark:hover:bg-hover-effect-dk whitespace-nowrap"
              >
                Export
              </button>
              <ViewToggle
                view={activeView}
                onViewChange={(v) => dispatch(setDealsActiveView(v))}
                viewOptions={[
                  { id: "grid" as const, icon: GridIcon, label: "Grid" },
                  { id: "list" as const, icon: ListViewIcon, label: "List" },
                ]}
              />
            </div>
          </div>
          {activeView === "list" ? (
            <div
              className="flex flex-col gap-5 flex-1 min-h-0 overflow-y-auto pb-4"
              data-crm-stage-scroll-container
            >
              {filteredStages?.map((stage) => (
                <DealTableListing
                  key={stage?.stage_key}
                  stage={stage}
                  sharedOwnerIds={sharedOwnerIds}
                  onOwnerApply={handleOwnerApply}
                  onOwnerReset={handleOwnerReset}
                  viewId={boardViewId}
                  sortKey={sortKey}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
              ))}
            </div>
          ) : (
            renderKanbanView()
          )}
        </div>
      </div>

      <DealsCreateEditView />

      <ExportContactsModal
        open={isExportModalOpen}
        onOpenChange={setIsExportModalOpen}
        totalCount={totalDealCount}
        defaultName={
          selectedViewId === "all_deals"
            ? DEAL_LABELS.allItems
            : selectedViewId === "my_deals"
              ? DEAL_LABELS.myItems
              : (dealsViews?.find((v) => v.id === selectedViewId)?.name ??
                "All Deals")
        }
        loading={isExporting}
        onExport={handleExport}
      />

      {exportedFile && (
        <ExportQueuedModal
          open={isExportQueuedOpen}
          onOpenChange={setIsExportQueuedOpen}
          entityName={exportedFile.name}
          fileName={exportedFile.name}
          onViewImportsExports={() =>
            navigate("/dashboard/settings/imports_and_exports")
          }
        />
      )}

      {deleteTarget && (
        <CrmDeleteViewModal
          open={!!deleteTarget}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          onConfirmDelete={handleConfirmDeleteView}
          view={deleteTarget}
          isDeleting={isDeletingView}
        />
      )}
    </>
  );
};

export default DealsLayout;
